import styled from 'styled-components';
import { Body } from '@workramp/ui';
import { sanitizeUserInput } from '~/utils/sanitize-user-input';
import { handleApiError } from '~/common';
import { RelativeTimeText } from '~/features/temporal';
import Avatar from '../common/avatar';
import NotificationsApi from '../../agent/notifications';

const Notification = ({ notification }) => {
  const { hasBeenClicked, orderingTime } = notification;

  const classes = !hasBeenClicked ? 'active' : '';

  const getAvatar = () => {
    const { data } = notification;

    const userData = data.user;
    return <Avatar name={userData.display_name} avatarSrc={userData.gravatar_url} />;
  };

  const handleViewNotificationClick = (e) => {
    const { data, id, type } = notification;

    e.preventDefault();
    if (['TestAnsweredNotification', 'FeedbackLeftNotification', 'CommentNotification'].includes(type)) {
      window.location.href = `/notifications/${id}`;
    } else {
      NotificationsApi.markClicked(
        id,
        () => {
          window.location.href = `/admin/guides/${data.course.id}/edit/${data.course_task.id}/`;
        },
        handleApiError,
      );
    }
  };

  const getInnerHtmlForCollabNotification = () => {
    const { data, type } = notification;

    // ['CollabBatchedNotification', 'CollabRealTimeNotification']
    const collabNotificationContext = {
      guideTitle: data.course.title,
    };
    switch (type) {
      case 'CollabBatchedNotification':
        return I18n.t('js.learner.notifications.dropdown.notification.collab_batched', collabNotificationContext); // TRANSLATED_BATCHED
      case 'CollabRealTimeNotification':
        return I18n.t('js.learner.notifications.dropdown.notification.collab_real_time', collabNotificationContext); // TRANSLATED_REAL_TIME;
      default:
        return '';
    }
  };

  const getInnerHtmlForTrainingNotification = () => {
    const { data, type } = notification;

    // ['TestAnsweredNotification', 'FeedbackLeftNotification', 'CommentNotification']
    let guideTitle = sanitizeUserInput(data.guide.display_title);

    if (guideTitle.length > 50) {
      guideTitle = `${guideTitle.substring(0, 49)}...`;
    }

    const trainingNotificationContext = {
      count: data.attachables_users_ids.length,
      user: sanitizeUserInput(data.user.display_name),
      content: guideTitle,
    };

    switch (type) {
      case 'TestAnsweredNotification':
        return I18n.t('js.learner.notifications.dropdown.notification.has_answered', trainingNotificationContext); // TRANSLATED_HAS_ANSWERED;
      case 'FeedbackLeftNotification':
        return I18n.t('js.learner.notifications.dropdown.notification.left_feedback', trainingNotificationContext); // TRANSLATED_LEFT_FEEDBACK;
      case 'CommentNotification':
        return I18n.t('js.learner.notifications.dropdown.notification.responded_feedback', trainingNotificationContext); // TRANSLATED_RESPONDED_FEEDBACK
      default:
        return '';
    }
  };

  const getInnerHtmlForNotification = () => {
    const { type } = notification;

    if (['TestAnsweredNotification', 'FeedbackLeftNotification', 'CommentNotification'].includes(type)) {
      return getInnerHtmlForTrainingNotification();
    }
    if (['CollabBatchedNotification', 'CollabRealTimeNotification'].includes(type)) {
      return getInnerHtmlForCollabNotification();
    }
    return '';
  };

  return (
    <ClickableNotification
      className={classes}
      onClick={handleViewNotificationClick}
      role="button"
      tabIndex={0}
      unread={!hasBeenClicked}
    >
      <AvatarWrapper>{getAvatar()}</AvatarWrapper>
      <NotificationInfo level="md">
        <NotificationText
          dangerouslySetInnerHTML={{
            __html: sanitizeUserInput(getInnerHtmlForNotification(), { allowSafeTags: true }),
          }}
        />
        <StyledDate>
          <RelativeTimeText date={new Date(orderingTime).valueOf()} />
        </StyledDate>
        <StyledDate>{hasBeenClicked}</StyledDate>
      </NotificationInfo>
    </ClickableNotification>
  );
};

const NotificationText = styled.div`
  span {
    font-weight: bold;
  }
`;

const ClickableNotification = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.spacing(2)}px;
  background: ${({ theme, unread }) => (unread ? theme.colors.primary[50] : theme.colors.surface[0])};
  border-bottom: 1px solid ${({ theme }) => theme.colors.on[100]};
  color: ${({ theme }) => theme.colors.on[800]};

  &:focus,
  &:hover {
    cursor: pointer;
    background: ${({ theme, unread }) => (unread ? theme.colors.primary[50] : theme.colors.surface[50])};
  }
`;

const NotificationInfo = styled(Body)`
  padding: ${({ theme }) => theme.spacing(2)}px;
`;

const AvatarWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing(2)}px;
`;

const StyledDate = styled.div`
  color: ${({ theme }) => theme.colors.primary[400]};
`;

export default Notification;
